const ATTACHMENT_UPLOAD_QUEUE_DB_NAME = 'nettgrav-messenger-v2-upload-queue';
const ATTACHMENT_UPLOAD_QUEUE_DB_VERSION = 2;
const ATTACHMENT_UPLOAD_QUEUE_JOB_STORE = 'jobs';
const ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE = 'attachment_payloads';
export const MESSENGER_V2_UPLOAD_SYNC_TAG = 'messenger-v2-attachment-uploads';

const memoryJobs = new Map();
const memoryPayloads = new Map();
let uploadQueueDbPromise = null;
let persistentStoragePromise = null;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const attachmentPayloadKey = (clientMsgId, index) => `${clientMsgId}:${index}`;

const cloneMetadata = (value) => {
  if (!value || typeof value !== 'object') return value;
  return {
    ...value,
    attachments: Array.isArray(value.attachments)
      ? value.attachments.map((attachment) => ({ ...attachment }))
      : [],
  };
};

const createQueueWriteError = (error = null) => {
  const queueError = new Error(
    'The attachments could not be stored safely on this device. Keep the app open while they upload.',
  );
  queueError.name = 'MessengerV2AttachmentQueueError';
  queueError.code = 'ATTACHMENT_QUEUE_WRITE_FAILED';
  queueError.stage = 'local_storage';
  queueError.cause = error || null;
  return queueError;
};

const splitJobAndPayloads = (job, durability = 'persistent') => {
  const clientMsgId = toStringOrNull(job?.clientMsgId);
  if (!clientMsgId) throw new Error('clientMsgId is required for an attachment upload job');

  const payloads = [];
  const attachments = (Array.isArray(job?.attachments) ? job.attachments : []).map(
    (attachment, index) => {
      const payloadKey =
        toStringOrNull(attachment?.payloadKey) || attachmentPayloadKey(clientMsgId, index);
      const { sourceFile = null, prepared = null, ...metadata } = attachment || {};
      if (sourceFile || prepared) {
        payloads.push({
          payloadKey,
          clientMsgId,
          attachmentIndex: index,
          sourceFile,
          prepared,
          updatedAt: Date.now(),
        });
      }
      return { ...metadata, payloadKey };
    },
  );

  return {
    metadata: {
      ...job,
      clientMsgId,
      attachments,
      schemaVersion: ATTACHMENT_UPLOAD_QUEUE_DB_VERSION,
      durability,
      createdAt: job.createdAt || new Date().toISOString(),
      updatedAt: Date.now(),
    },
    payloads,
  };
};

const migrateLegacyJobs = (transaction) => {
  const jobStore = transaction.objectStore(ATTACHMENT_UPLOAD_QUEUE_JOB_STORE);
  const payloadStore = transaction.objectStore(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE);
  const cursorRequest = jobStore.openCursor();
  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result;
    if (!cursor) return;
    const legacyJob = cursor.value;
    const { metadata, payloads } = splitJobAndPayloads(legacyJob);
    payloads.forEach((payload) => payloadStore.put(payload));
    cursor.update(metadata);
    cursor.continue();
  };
};

const openAttachmentUploadQueueDb = () => {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (uploadQueueDbPromise) return uploadQueueDbPromise;

  uploadQueueDbPromise = new Promise((resolve) => {
    let settled = false;
    let blockedTimer = null;
    const finish = (value) => {
      if (settled) {
        value?.close?.();
        return;
      }
      settled = true;
      if (blockedTimer) clearTimeout(blockedTimer);
      resolve(value);
    };
    const request = indexedDB.open(
      ATTACHMENT_UPLOAD_QUEUE_DB_NAME,
      ATTACHMENT_UPLOAD_QUEUE_DB_VERSION,
    );
    request.onupgradeneeded = (event) => {
      const db = request.result;
      const transaction = request.transaction;
      if (!db.objectStoreNames.contains(ATTACHMENT_UPLOAD_QUEUE_JOB_STORE)) {
        const store = db.createObjectStore(ATTACHMENT_UPLOAD_QUEUE_JOB_STORE, {
          keyPath: 'clientMsgId',
        });
        store.createIndex('updatedAt', 'updatedAt');
      }
      if (!db.objectStoreNames.contains(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE)) {
        const payloadStore = db.createObjectStore(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE, {
          keyPath: 'payloadKey',
        });
        payloadStore.createIndex('clientMsgId', 'clientMsgId');
        if (event.oldVersion > 0 && transaction) migrateLegacyJobs(transaction);
      }
    };
    request.onerror = () => {
      uploadQueueDbPromise = null;
      finish(null);
    };
    request.onblocked = () => {
      blockedTimer = setTimeout(() => {
        uploadQueueDbPromise = null;
        finish(null);
      }, 2000);
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        uploadQueueDbPromise = null;
      };
      finish(db);
    };
  });

  return uploadQueueDbPromise;
};

const requestPersistentAttachmentStorage = async () => {
  if (persistentStoragePromise) return persistentStoragePromise;
  const storage = typeof navigator !== 'undefined' ? navigator.storage : null;
  if (typeof storage?.persist !== 'function') return false;
  persistentStoragePromise = Promise.resolve(storage.persist()).catch(() => false);
  return persistentStoragePromise;
};

const rememberVolatileJob = (job) => {
  const { metadata, payloads } = splitJobAndPayloads(job, 'memory');
  memoryJobs.set(metadata.clientMsgId, cloneMetadata(metadata));
  payloads.forEach((payload) => memoryPayloads.set(payload.payloadKey, payload));
  return metadata;
};

const readRequest = (request, fallback = null) =>
  new Promise((resolve) => {
    request.onerror = () => resolve(fallback);
    request.onsuccess = () => resolve(request.result ?? fallback);
  });

const writePersistentRecord = (db, storeName, value) =>
  new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const request = transaction.objectStore(storeName).put(value);
    transaction.oncomplete = () => resolve(value);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
    request.onerror = () => reject(request.error || transaction.error);
  });

const deletePersistentPayloads = async (db, payloadKeys) => {
  for (const payloadKey of payloadKeys) {
    try {
      // Keep cleanup transactions small for the same reason payload writes are serialized.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        const transaction = db.transaction(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE, 'readwrite');
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
        transaction.objectStore(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE).delete(payloadKey);
      });
    } catch {}
  }
};

export const saveMessengerV2AttachmentUploadJob = async (job = null) => {
  const { metadata, payloads } = splitJobAndPayloads(job);
  if (memoryJobs.has(metadata.clientMsgId)) return rememberVolatileJob(job);
  await requestPersistentAttachmentStorage();
  const db = await openAttachmentUploadQueueDb();
  if (!db) return rememberVolatileJob(job);

  const persistedPayloadKeys = [];
  try {
    for (const payload of payloads) {
      // One Blob per transaction prevents Safari from cloning a whole media batch at once.
      // eslint-disable-next-line no-await-in-loop
      await writePersistentRecord(db, ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE, payload);
      persistedPayloadKeys.push(payload.payloadKey);
    }
    await writePersistentRecord(db, ATTACHMENT_UPLOAD_QUEUE_JOB_STORE, metadata);
    memoryJobs.delete(metadata.clientMsgId);
    payloads.forEach((payload) => memoryPayloads.delete(payload.payloadKey));
    return metadata;
  } catch (error) {
    await deletePersistentPayloads(db, persistedPayloadKeys);
    const volatileJob = rememberVolatileJob(job);
    return {
      ...volatileJob,
      storageWarning: createQueueWriteError(error).message,
    };
  }
};

export const getMessengerV2AttachmentUploadJob = async (clientMsgId = null) => {
  const normalizedId = toStringOrNull(clientMsgId);
  if (!normalizedId) return null;
  if (memoryJobs.has(normalizedId)) return cloneMetadata(memoryJobs.get(normalizedId));
  const db = await openAttachmentUploadQueueDb();
  if (!db) return null;
  try {
    const transaction = db.transaction(ATTACHMENT_UPLOAD_QUEUE_JOB_STORE, 'readonly');
    return await readRequest(
      transaction.objectStore(ATTACHMENT_UPLOAD_QUEUE_JOB_STORE).get(normalizedId),
    );
  } catch {
    return null;
  }
};

export const getMessengerV2AttachmentUploadPayload = async (clientMsgId, index) => {
  const normalizedId = toStringOrNull(clientMsgId);
  if (!normalizedId) return null;
  const payloadKey = attachmentPayloadKey(normalizedId, index);
  if (memoryPayloads.has(payloadKey)) return memoryPayloads.get(payloadKey);
  const db = await openAttachmentUploadQueueDb();
  if (!db) return null;
  try {
    const transaction = db.transaction(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE, 'readonly');
    return await readRequest(
      transaction.objectStore(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE).get(payloadKey),
    );
  } catch {
    return null;
  }
};

export const updateMessengerV2AttachmentUploadPayload = async (clientMsgId, index, update) => {
  const normalizedId = toStringOrNull(clientMsgId);
  if (!normalizedId) return null;
  const payloadKey = attachmentPayloadKey(normalizedId, index);
  if (memoryJobs.has(normalizedId)) {
    const current = memoryPayloads.get(payloadKey) || {
      payloadKey,
      clientMsgId: normalizedId,
      attachmentIndex: index,
    };
    const next = {
      ...current,
      ...(typeof update === 'function' ? update(current) : update),
      updatedAt: Date.now(),
    };
    memoryPayloads.set(payloadKey, next);
    return next;
  }

  const db = await openAttachmentUploadQueueDb();
  if (!db) return null;
  return new Promise((resolve, reject) => {
    let nextPayload = null;
    const transaction = db.transaction(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE, 'readwrite');
    const store = transaction.objectStore(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE);
    const request = store.get(payloadKey);
    transaction.onerror = () => reject(createQueueWriteError(transaction.error));
    transaction.onabort = () => reject(createQueueWriteError(transaction.error));
    transaction.oncomplete = () => resolve(nextPayload);
    request.onerror = () => reject(createQueueWriteError(request.error));
    request.onsuccess = () => {
      const current = request.result || {
        payloadKey,
        clientMsgId: normalizedId,
        attachmentIndex: index,
      };
      nextPayload = {
        ...current,
        ...(typeof update === 'function' ? update(current) : update),
        updatedAt: Date.now(),
      };
      store.put(nextPayload);
    };
  });
};

export const removeMessengerV2AttachmentUploadPayload = async (clientMsgId, index) => {
  const normalizedId = toStringOrNull(clientMsgId);
  if (!normalizedId) return false;
  const payloadKey = attachmentPayloadKey(normalizedId, index);
  memoryPayloads.delete(payloadKey);
  const db = await openAttachmentUploadQueueDb();
  if (!db) return true;
  try {
    await new Promise((resolve) => {
      const transaction = db.transaction(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE, 'readwrite');
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
      transaction.objectStore(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE).delete(payloadKey);
    });
  } catch {}
  return true;
};

export const listMessengerV2AttachmentUploadJobs = async () => {
  const db = await openAttachmentUploadQueueDb();
  let persistentJobs = [];
  if (db) {
    try {
      const transaction = db.transaction(ATTACHMENT_UPLOAD_QUEUE_JOB_STORE, 'readonly');
      persistentJobs = await readRequest(
        transaction.objectStore(ATTACHMENT_UPLOAD_QUEUE_JOB_STORE).getAll(),
        [],
      );
    } catch {}
  }
  const merged = new Map(
    [...persistentJobs, ...memoryJobs.values()].map((job) => [job.clientMsgId, cloneMetadata(job)]),
  );
  return Array.from(merged.values())
    .filter((job) => toStringOrNull(job?.clientMsgId))
    .sort((left, right) => Number(left.updatedAt || 0) - Number(right.updatedAt || 0));
};

export const updateMessengerV2AttachmentUploadJob = async (clientMsgId, updater) => {
  const normalizedId = toStringOrNull(clientMsgId);
  if (!normalizedId) return null;
  if (memoryJobs.has(normalizedId)) {
    const current = cloneMetadata(memoryJobs.get(normalizedId));
    const updated = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
    if (!updated) return null;
    const { metadata } = splitJobAndPayloads({ ...updated, durability: 'memory' }, 'memory');
    memoryJobs.set(normalizedId, metadata);
    return cloneMetadata(metadata);
  }

  const db = await openAttachmentUploadQueueDb();
  if (!db) return null;
  return new Promise((resolve, reject) => {
    let nextJob = null;
    const transaction = db.transaction(ATTACHMENT_UPLOAD_QUEUE_JOB_STORE, 'readwrite');
    const store = transaction.objectStore(ATTACHMENT_UPLOAD_QUEUE_JOB_STORE);
    const request = store.get(normalizedId);
    transaction.onerror = () => reject(createQueueWriteError(transaction.error));
    transaction.onabort = () => reject(createQueueWriteError(transaction.error));
    transaction.oncomplete = () => resolve(nextJob);
    request.onerror = () => reject(createQueueWriteError(request.error));
    request.onsuccess = () => {
      const current = request.result;
      if (!current) return;
      const updated = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      if (!updated) return;
      const { metadata } = splitJobAndPayloads(updated);
      nextJob = metadata;
      store.put(nextJob);
    };
  });
};

export const removeMessengerV2AttachmentUploadJob = async (clientMsgId = null) => {
  const normalizedId = toStringOrNull(clientMsgId);
  if (!normalizedId) return false;
  const job = await getMessengerV2AttachmentUploadJob(normalizedId);
  memoryJobs.delete(normalizedId);
  (job?.attachments || []).forEach((attachment, index) => {
    memoryPayloads.delete(attachment?.payloadKey || attachmentPayloadKey(normalizedId, index));
  });
  const db = await openAttachmentUploadQueueDb();
  if (!db) return true;
  try {
    await new Promise((resolve) => {
      const transaction = db.transaction(
        [ATTACHMENT_UPLOAD_QUEUE_JOB_STORE, ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE],
        'readwrite',
      );
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
      transaction.objectStore(ATTACHMENT_UPLOAD_QUEUE_JOB_STORE).delete(normalizedId);
      const payloadStore = transaction.objectStore(ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE);
      (job?.attachments || []).forEach((attachment, index) => {
        payloadStore.delete(attachment?.payloadKey || attachmentPayloadKey(normalizedId, index));
      });
    });
  } catch {}
  return true;
};

export const requestMessengerV2AttachmentBackgroundSync = async ({ processNow = false } = {}) => {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    if (typeof registration?.sync?.register === 'function') {
      await registration.sync.register(MESSENGER_V2_UPLOAD_SYNC_TAG);
    }
    if (processNow) {
      registration?.active?.postMessage?.({ type: 'MESSENGER_V2_PROCESS_UPLOAD_QUEUE' });
    }
    return true;
  } catch {
    return false;
  }
};

export const resetMessengerV2AttachmentUploadQueueForTests = () => {
  memoryJobs.clear();
  memoryPayloads.clear();
  uploadQueueDbPromise = null;
  persistentStoragePromise = null;
};

export const messengerV2AttachmentUploadQueueConfig = {
  dbName: ATTACHMENT_UPLOAD_QUEUE_DB_NAME,
  dbVersion: ATTACHMENT_UPLOAD_QUEUE_DB_VERSION,
  jobStoreName: ATTACHMENT_UPLOAD_QUEUE_JOB_STORE,
  payloadStoreName: ATTACHMENT_UPLOAD_QUEUE_PAYLOAD_STORE,
};
